import { useState, useContext, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ClienteFormContext } from "../../../context/cliente/ClienteContext";
import { getProvincias } from "../../../services/ProvinciaServices";
import { getDepartamentos } from "../../../services/DepartamentosServices";
import { getLocalidades } from "../../../services/LocalidadesServices";
import { getBarrios } from "../../../services/BarriosServices";
import { getTipoDocumentos } from "../../../services/TipoDocumentoServices";
import { AuthContext } from "../../../context/authContext";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { validation } from "./helpers/validation";
import {
  Box, Typography, Grid, Paper, TextField, Button,
  MenuItem, Chip, useMediaQuery, useTheme,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import OrderCache from "../../orderCache/order";
import OrderHistory from "../../orderHistory/orderHistory";
import LogoutConfirmationModal from "../../../components/ConfirmationModal/LogoutConfirmationModal";
import ChangePass from "./changePass";

// ─── Estilos ──────────────────────────────────────────────────────────────────
const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px", backgroundColor: "#f8fafc", transition: "all 0.18s ease",
    "& fieldset": { border: "1.5px solid #e2e8f0" },
    "&:hover fieldset": { borderColor: "#94a3b8" },
    "&.Mui-focused fieldset": { borderColor: "var(--accent)", borderWidth: "2px" },
    "&.Mui-focused": { backgroundColor: "#fff" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.85rem", "&.Mui-focused": { color: "var(--accent)" } },
  "& .MuiFormHelperText-root": { fontSize: "0.72rem" },
};

const selectMenuProps = {
  PaperProps: { sx: { maxHeight: 48 * 5.5, borderRadius: "12px", boxShadow: "0 10px 30px rgba(0,0,0,0.12)", border: "1px solid #e2e8f0" } },
};

// ─── 6 secciones ─────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { key: "dpersonales", icon: PersonOutlineIcon, label: "Mis datos" },
  { key: "denvio", icon: HomeOutlinedIcon, label: "Dirección" },
  { key: "dentrega", icon: LocalShippingOutlinedIcon, label: "Entrega" },
  { key: "dpedidos", icon: ShoppingBagOutlinedIcon, label: "Pedidos" },
  { key: "dhistorial", icon: HistoryOutlinedIcon, label: "Historial" },
  { key: "dacceso", icon: LockOutlinedIcon, label: "Contraseña" },
  { key: "logout", icon: LogoutIcon, label: "Salir", isLogout: true },
];

// URL segment ↔ tab key
const URL_TO_TAB = {
  datos: "dpersonales",
  envio: "denvio",
  entrega: "dentrega",
  pedidos: "dpedidos",
  historial: "dhistorial",
  contrasena: "dacceso",
};
const TAB_TO_URL = Object.fromEntries(Object.entries(URL_TO_TAB).map(([u, t]) => [t, u]));

// ─── Helpers UI ───────────────────────────────────────────────────────────────
const SectionHeader = ({ icon: Icon, title }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3, pb: 2, borderBottom: "1.5px solid #f1f5f9" }}>
    <Box sx={{ width: 38, height: 38, borderRadius: "10px", bgcolor: "var(--accent-soft)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <Icon sx={{ fontSize: 20, color: "var(--accent)" }} />
    </Box>
    <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: "var(--text-primary)", letterSpacing: "-0.01em", fontFamily: "var(--font-heading)" }}>
      {title}
    </Typography>
  </Box>
);

const FieldGroupLabel = ({ label }) => (
  <Grid item xs={12}>
    <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.07em", mt: 1, mb: -0.5, fontFamily: "var(--font-body)" }}>
      {label}
    </Typography>
  </Grid>
);

// ─── Nav vertical (desktop) ───────────────────────────────────────────────────
const NavList = ({ active, onSelect, onLogout }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, p: 1 }}>
    {NAV_ITEMS.map(({ key, icon: Icon, label, isLogout }) => {
      const isActive = active === key;
      return (
        <Box key={key} onClick={() => isLogout ? onLogout() : onSelect(key)}
          sx={{
            display: "flex", alignItems: "center", gap: 1.5,
            px: 1.5, py: 1.1, borderRadius: "10px", cursor: "pointer",
            transition: "all 0.15s ease",
            color: isLogout ? "#ef4444" : isActive ? "var(--accent)" : "var(--text-secondary)",
            bgcolor: isActive ? "var(--accent-soft)" : "transparent",
            "&:hover": { bgcolor: isLogout ? "#fef2f2" : isActive ? "var(--accent-soft)" : "var(--bg-subtle)" },
          }}
        >
          <Icon sx={{ fontSize: 19, flexShrink: 0 }} />
          <Typography sx={{ fontSize: "0.85rem", fontWeight: isActive ? 700 : 500, color: "inherit", fontFamily: "var(--font-body)" }}>
            {label}
          </Typography>
          {isActive && !isLogout && (
            <Box sx={{ ml: "auto", width: 6, height: 6, borderRadius: "50%", bgcolor: "var(--accent)" }} />
          )}
        </Box>
      );
    })}
  </Box>
);

// ─── Nav horizontal scrollable (mobile) ──────────────────────────────────────
const MobileTabBar = ({ active, onSelect, onLogout }) => {
  const scrollRef = useRef(null);
  const scroll = (dir) => scrollRef.current?.scrollBy({ left: dir === "left" ? -140 : 140, behavior: "smooth" });

  return (
    <Box sx={{ position: "relative", mb: 2, borderBottom: "1.5px solid var(--border)" }}>
      <Box onClick={() => scroll("left")} sx={{ position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)", zIndex: 2, width: 28, height: 28, bgcolor: "var(--bg-card)", border: "1.5px solid var(--border)", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", "&:hover": { bgcolor: "var(--accent-soft)" } }}>
        <ChevronLeftIcon sx={{ fontSize: 18, color: "var(--text-muted)" }} />
      </Box>
      <Box ref={scrollRef} sx={{ display: "flex", overflowX: "auto", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" }, gap: 0.5, px: 4, py: 0.75 }}>
        {NAV_ITEMS.map(({ key, icon: Icon, label, isLogout }) => {
          const isActive = active === key;
          return (
            <Box key={key} onClick={() => isLogout ? onLogout() : onSelect(key)}
              sx={{
                display: "flex", alignItems: "center", gap: 0.75,
                px: 1.25, py: 0.75, borderRadius: "10px",
                cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap",
                color: isLogout ? "#ef4444" : isActive ? "var(--accent)" : "var(--text-secondary)",
                bgcolor: isActive ? "var(--accent-soft)" : "transparent",
                border: isActive ? "1.5px solid var(--accent)" : "1.5px solid transparent",
                "&:hover": { bgcolor: isLogout ? "#fef2f2" : "var(--bg-subtle)" },
              }}
            >
              <Icon sx={{ fontSize: 17, flexShrink: 0 }} />
              <Typography sx={{ fontSize: "0.8rem", fontWeight: isActive ? 700 : 500, color: "inherit", fontFamily: "var(--font-body)", lineHeight: 1 }}>
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>
      <Box onClick={() => scroll("right")} sx={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)", zIndex: 2, width: 28, height: 28, bgcolor: "var(--bg-card)", border: "1.5px solid var(--border)", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", "&:hover": { bgcolor: "var(--accent-soft)" } }}>
        <ChevronRightIcon sx={{ fontSize: 18, color: "var(--text-muted)" }} />
      </Box>
    </Box>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
export const FormProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, clienteSelected, getMeData, user } = useContext(AuthContext);
  const { patchClienteData, getClienteByIdData, changePassword } = useContext(ClienteFormContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const getTabFromUrl = () => {
    const segment = location.pathname.split("/").filter(Boolean).pop();
    return URL_TO_TAB[segment] || "dpersonales";
  };

  const [seccionActiva, setSeccionActiva] = useState(getTabFromUrl);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [provincias, setProvincias] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [departamentosEntrega, setDepartamentosEntrega] = useState([]);
  const [localidades, setLocalidades] = useState([]);
  const [localidadesEntrega, setLocalidadesEntrega] = useState([]);
  const [barrios, setBarrios] = useState([]);
  const [barriosEntrega, setBarriosEntrega] = useState([]);

  useEffect(() => { setSeccionActiva(getTabFromUrl()); }, [location.pathname]);

  const handleTabChange = (key) => {
    const segment = TAB_TO_URL[key];
    navigate(segment ? `/profile/${segment}` : "/profile");
  };

  const { register, handleSubmit, control, reset, formState: { errors }, watch } = useForm({
    mode: "onChange", resolver: yupResolver(validation),
  });

  const provinciaSeleccionada = useWatch({ control, name: "pcia_id" });
  const departamentosSeleccionado = useWatch({ control, name: "depto_id" });
  const localidadSeleccionada = useWatch({ control, name: "loc_id" });
  const barriosSeleccionado = useWatch({ control, name: "barrio_id" });
  const provinciaEntregaSeleccionada = useWatch({ control, name: "cli_pcia_entrega" });
  const dptoEntregaSeleccionado = useWatch({ control, name: "cli_depto_entrega" });
  const localidadEntregaSeleccionada = useWatch({ control, name: "cli_loc_entrega" });
  const barrioEntregaSeleccionado = useWatch({ control, name: "cli_barrio_entrega" });
  const tipoIvaSeleccionado = useWatch({ control, name: "tipoiva_id" });

  // ── Data fetching ────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetch = async () => {
      const clienteId = clienteSelected?.id || user?.id;
      if (!clienteId) return;
      const data = await getClienteByIdData(clienteId);
      reset({
        ...data,
        pcia_id: data.pcia_id != null ? String(data.pcia_id) : null,
        depto_id: data.depto_id != null ? String(data.depto_id) : null,
        loc_id: data.loc_id != null ? String(data.loc_id) : null,
        barrio_id: data.barrio_id != null ? String(data.barrio_id) : null,
        cli_pcia_entrega: data.cli_pcia_entrega != null ? String(data.cli_pcia_entrega) : null,
        cli_depto_entrega: data.cli_depto_entrega != null ? String(data.cli_depto_entrega) : null,
        cli_loc_entrega: data.cli_loc_entrega != null ? String(data.cli_loc_entrega) : null,
        cli_barrio_entrega: data.cli_barrio_entrega != null ? String(data.cli_barrio_entrega) : null,
        tipoiva_id: data.tipoiva_id != null ? String(data.tipoiva_id) : "",
        tipo_doc: data.tipo_doc != null ? String(data.tipo_doc) : "",
      });
    };
    fetch();
  }, [user?.id, clienteSelected, reset]);

  useEffect(() => { getProvincias().then(r => setProvincias(r || [])).catch(console.error); }, []);
  useEffect(() => { if (provinciaSeleccionada) getDepartamentos(provinciaSeleccionada).then(r => setDepartamentos(r || [])); else setDepartamentos([]); }, [provinciaSeleccionada]);
  useEffect(() => { if (departamentosSeleccionado) getLocalidades(departamentosSeleccionado).then(r => setLocalidades(r || [])); else setLocalidades([]); }, [departamentosSeleccionado]);
  useEffect(() => { if (localidadSeleccionada) getBarrios(localidadSeleccionada).then(r => setBarrios(r || [])); else setBarrios([]); }, [localidadSeleccionada]);
  useEffect(() => { if (provinciaEntregaSeleccionada) getDepartamentos(provinciaEntregaSeleccionada).then(r => setDepartamentosEntrega(r || [])); else setDepartamentosEntrega([]); }, [provinciaEntregaSeleccionada]);
  useEffect(() => { if (dptoEntregaSeleccionado) getLocalidades(dptoEntregaSeleccionado).then(r => setLocalidadesEntrega(r || [])); else setLocalidadesEntrega([]); }, [dptoEntregaSeleccionado]);
  useEffect(() => { if (localidadEntregaSeleccionada) getBarrios(localidadEntregaSeleccionada).then(r => setBarriosEntrega(r || [])); else setBarriosEntrega([]); }, [localidadEntregaSeleccionada]);

  const onSubmit = async (data) => {
    const cleanedData = Object.fromEntries(Object.entries(data).filter(([_, v]) => v !== null && v !== undefined));
    const id = clienteSelected?.id || user?.id;
    await patchClienteData(id, cleanedData);
    await getMeData();
    navigate("/profile");
  };

  const userName = user?.sel_lastname ? `${user.sel_lastname} ${user.sel_firstname}` : user?.sel_firstname || "";
  const initials = userName.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase() || "U";
  const needsForm = ["dpersonales", "denvio", "dentrega"].includes(seccionActiva);

  // ── Sidebar ──────────────────────────────────────────────────────────────────
  const Sidebar = () => (
    <Box>
      <Box sx={{ px: 2, pt: 2.5, pb: 2, borderBottom: "1.5px solid var(--border)", display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box sx={{ width: 42, height: 42, borderRadius: "50%", flexShrink: 0, background: "linear-gradient(135deg, var(--accent) 0%, var(--purple) 100%)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: "15px", fontFamily: "var(--font-heading)" }}>
          {initials}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "var(--text-primary)", fontFamily: "var(--font-heading)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {userName}
          </Typography>
          {clienteSelected && (
            <Chip label={clienteSelected.cli_name} size="small" sx={{ mt: 0.25, height: 18, fontSize: "0.68rem", fontWeight: 600, bgcolor: "var(--accent-soft)", color: "var(--accent)" }} />
          )}
        </Box>
      </Box>
      <NavList active={seccionActiva} onSelect={handleTabChange} onLogout={() => setShowLogoutModal(true)} />
    </Box>
  );

  // ── Botón guardar ─────────────────────────────────────────────────────────────
  const SaveBtn = () => (
    <Box sx={{ mt: 3.5, display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" } }}>
      <Button type="submit" variant="contained" startIcon={<SaveOutlinedIcon sx={{ fontSize: 17 }} />}
        fullWidth={isMobile}
        sx={{ borderRadius: "var(--radius-md)", textTransform: "none", fontWeight: 700, fontSize: "0.9rem", px: { xs: 2, sm: 4 }, py: 1.2, bgcolor: "var(--accent)", fontFamily: "var(--font-body)", boxShadow: "var(--shadow-glow)", "&:hover": { bgcolor: "var(--accent-hover)", transform: "translateY(-1px)" }, transition: "all 0.18s ease" }}>
        Guardar cambios
      </Button>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1100, px: { xs: 1.5, sm: 2, md: 3 }, py: { xs: 2, md: 3.5 } }}>

      {isMobile && <MobileTabBar active={seccionActiva} onSelect={handleTabChange} onLogout={() => setShowLogoutModal(true)} />}

      <LogoutConfirmationModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={() => { logout(); setShowLogoutModal(false); }}
      />

      <Grid container spacing={2.5} alignItems="flex-start">

        {/* Sidebar desktop */}
        {!isMobile && (
          <Grid item md={3}>
            <Paper elevation={0} sx={{ borderRadius: "var(--radius-xl)", border: "1.5px solid var(--border)", overflow: "hidden", position: "sticky", top: 90 }}>
              <Sidebar />
            </Paper>
          </Grid>
        )}

        {/* Contenido */}
        <Grid item xs={12} md={9} sx={{ minWidth: 0 }}>

          {/* ── Contraseña ── */}
          {seccionActiva === "dacceso" && (
            <Paper elevation={0} sx={{ p: { xs: 2, md: 3.5 }, borderRadius: "var(--radius-xl)", border: "1.5px solid var(--border)" }}>
              <SectionHeader icon={LockOutlinedIcon} title="Cambiar Contraseña" />
              <ChangePass update={changePassword} logout={logout} />
            </Paper>
          )}

          {/* ── Pedidos ── */}
          {seccionActiva === "dpedidos" && (
            <Paper elevation={0} sx={{ p: { xs: 2, md: 3.5 }, borderRadius: "var(--radius-xl)", border: "1.5px solid var(--border)", overflowX: "auto" }}>
              <OrderCache />
            </Paper>
          )}

          {/* ── Historial ── */}
          {seccionActiva === "dhistorial" && (
            <Paper elevation={0} sx={{ p: { xs: 2, md: 3.5 }, borderRadius: "var(--radius-xl)", border: "1.5px solid var(--border)", overflowX: "auto" }}>
              <OrderHistory />
            </Paper>
          )}

          {/* ── Formularios ── */}
          {needsForm && (
            <form onSubmit={handleSubmit(onSubmit)}>
              <Paper elevation={0} sx={{ p: { xs: 2, sm: 2.5, md: 3.5 }, borderRadius: "var(--radius-xl)", border: "1.5px solid var(--border)" }}>

                {/* ══ DATOS PERSONALES ══ */}
                {seccionActiva === "dpersonales" && (<>
                  <SectionHeader icon={PersonOutlineIcon} title="Datos Personales" />
                  <Grid container spacing={2}>
                    <FieldGroupLabel label="Información General" />

                    <Grid item xs={12} sm={6}>
                      <TextField label="Razón Social" fullWidth size="small" {...register("razon_social")} value={watch("razon_social") || ""} InputLabelProps={{ shrink: true }} sx={inputSx} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Nombre Completo" fullWidth size="small" {...register("cli_name")} value={watch("cli_name") || ""} error={!!errors.cli_name} helperText={errors.cli_name?.message} InputLabelProps={{ shrink: true }} sx={inputSx} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select label="Tipo IVA" fullWidth size="small" {...register("tipoiva_id")} value={tipoIvaSeleccionado || ""} InputLabelProps={{ shrink: true }} sx={inputSx} SelectProps={{ MenuProps: selectMenuProps }}>
                        <MenuItem value={1}>Responsable Inscripto</MenuItem>
                        <MenuItem value={5}>Monotributista</MenuItem>
                        <MenuItem value={4}>Consumidor Final</MenuItem>
                        <MenuItem value={2}>Exento</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Nro. Documento / CUIT" fullWidth size="small" {...register("cli_cuit")} value={watch("cli_cuit") || ""} error={!!errors.cli_cuit} helperText={errors.cli_cuit?.message} InputLabelProps={{ shrink: true }} sx={inputSx} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Correo electrónico" fullWidth size="small" {...register("cli_mail")} value={watch("cli_mail") || ""} error={!!errors.cli_mail} helperText={errors.cli_mail?.message} InputLabelProps={{ shrink: true }} sx={inputSx} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Teléfono" fullWidth size="small" {...register("cli_tel")} value={watch("cli_tel") || ""} error={!!errors.cli_tel} helperText={errors.cli_tel?.message} InputLabelProps={{ shrink: true }} sx={inputSx} />
                    </Grid>
                  </Grid>
                  <SaveBtn />
                </>)}

                {/* ══ DIRECCIÓN FISCAL ══ */}
                {seccionActiva === "denvio" && (<>
                  <SectionHeader icon={HomeOutlinedIcon} title="Dirección Fiscal" />
                  <Grid container spacing={2}>
                    <FieldGroupLabel label="Dirección Principal" />
                    <Grid item xs={12} sm={4}>
                      <TextField label="Calle" fullWidth size="small" {...register("cli_direccion")} value={watch("cli_direccion") || ""} InputLabelProps={{ shrink: true }} sx={inputSx} />
                    </Grid>
                    <Grid item xs={3} sm={2}>
                      <TextField label="Nro" fullWidth size="small" {...register("cli_nro_calle")} InputLabelProps={{ shrink: true }} sx={inputSx} />
                    </Grid>
                    <Grid item xs={3} sm={2}>
                      <TextField label="Piso" fullWidth size="small" {...register("cli_piso")} InputLabelProps={{ shrink: true }} sx={inputSx} />
                    </Grid>
                    <Grid item xs={3} sm={2}>
                      <TextField label="Dpto" fullWidth size="small" {...register("cli_dpto")} InputLabelProps={{ shrink: true }} sx={inputSx} />
                    </Grid>
                    <Grid item xs={3} sm={2}>
                      <TextField label="C.P." fullWidth size="small" {...register("cli_cp")} InputLabelProps={{ shrink: true }} sx={inputSx} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField select label="Provincia" fullWidth size="small" {...register("pcia_id")} value={provinciaSeleccionada || ""} sx={inputSx} SelectProps={{ MenuProps: selectMenuProps }}>
                        {provincias.map(p => <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField select label="Departamento" fullWidth size="small" {...register("depto_id")} value={departamentosSeleccionado || ""} disabled={!departamentos.length} sx={inputSx} SelectProps={{ MenuProps: selectMenuProps }}>
                        {departamentos.map(d => <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField select label="Localidad" fullWidth size="small" {...register("loc_id")} value={localidadSeleccionada || ""} disabled={!localidades.length} sx={inputSx} SelectProps={{ MenuProps: selectMenuProps }}>
                        {localidades.map(l => <MenuItem key={l.id} value={l.id}>{l.label}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField select label="Barrio" fullWidth size="small" {...register("barrio_id")} value={barriosSeleccionado || ""} disabled={!barrios.length} sx={inputSx} SelectProps={{ MenuProps: selectMenuProps }}>
                        {barrios.map(b => <MenuItem key={b.id} value={b.id}>{b.label}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField label="Observación" fullWidth size="small" {...register("cli_direccion_observ")} value={watch("cli_direccion_observ") || ""} InputLabelProps={{ shrink: true }} sx={inputSx} />
                    </Grid>
                  </Grid>
                  <SaveBtn />
                </>)}

                {/* ══ DIRECCIÓN DE ENTREGA ══ */}
                {seccionActiva === "dentrega" && (<>
                  <SectionHeader icon={LocalShippingOutlinedIcon} title="Dirección de Entrega" />
                  <Grid container spacing={2}>
                    <FieldGroupLabel label="Domicilio de Entrega" />
                    <Grid item xs={12} sm={4}>
                      <TextField label="Calle" fullWidth size="small" {...register("cli_direccion_entrega")} value={watch("cli_direccion_entrega") || ""} InputLabelProps={{ shrink: true }} sx={inputSx} />
                    </Grid>
                    <Grid item xs={3} sm={2}>
                      <TextField label="Nro" fullWidth size="small" {...register("cli_nro_calle_entrega")} InputLabelProps={{ shrink: true }} sx={inputSx} />
                    </Grid>
                    <Grid item xs={3} sm={2}>
                      <TextField label="Piso" fullWidth size="small" {...register("cli_piso_entrega")} InputLabelProps={{ shrink: true }} sx={inputSx} />
                    </Grid>
                    <Grid item xs={3} sm={2}>
                      <TextField label="Dpto" fullWidth size="small" {...register("cli_dpto_entrega")} InputLabelProps={{ shrink: true }} sx={inputSx} />
                    </Grid>
                    <Grid item xs={3} sm={2}>
                      <TextField label="C.P." fullWidth size="small" {...register("cli_cp_entrega")} InputLabelProps={{ shrink: true }} sx={inputSx} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField select label="Provincia" fullWidth size="small" {...register("cli_pcia_entrega")} value={provinciaEntregaSeleccionada || ""} sx={inputSx} SelectProps={{ MenuProps: selectMenuProps }}>
                        {provincias.map(p => <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField select label="Departamento" fullWidth size="small" {...register("cli_depto_entrega")} value={dptoEntregaSeleccionado || ""} disabled={!departamentosEntrega.length} sx={inputSx} SelectProps={{ MenuProps: selectMenuProps }}>
                        {departamentosEntrega.map(d => <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField select label="Localidad" fullWidth size="small" {...register("cli_loc_entrega")} value={localidadEntregaSeleccionada || ""} disabled={!localidadesEntrega.length} sx={inputSx} SelectProps={{ MenuProps: selectMenuProps }}>
                        {localidadesEntrega.map(l => <MenuItem key={l.id} value={l.id}>{l.label}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField select label="Barrio" fullWidth size="small" {...register("cli_barrio_entrega")} value={barrioEntregaSeleccionado || ""} disabled={!barriosEntrega.length} sx={inputSx} SelectProps={{ MenuProps: selectMenuProps }}>
                        {barriosEntrega.map(b => <MenuItem key={b.id} value={b.id}>{b.label}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField label="Observación de entrega" fullWidth size="small" {...register("cli_mail_entrega")} value={watch("cli_mail_entrega") || ""} InputLabelProps={{ shrink: true }} sx={inputSx} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Email de entrega" fullWidth size="small" {...register("cli_mail_entrega")} value={watch("cli_mail_entrega") || ""} InputLabelProps={{ shrink: true }} sx={inputSx} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Teléfono de entrega" fullWidth size="small" {...register("cli_tel_entrega")} value={watch("cli_tel_entrega") || ""} InputLabelProps={{ shrink: true }} sx={inputSx} />
                    </Grid>
                  </Grid>
                  <SaveBtn />
                </>)}

              </Paper>
            </form>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};